import { useCallback, useRef } from 'react';

import { useAuth } from '@/lib/auth/client-auth';

/**
 * Client-side API routing.
 *
 * Every server route lives in Convex now (docs/CONVEX-MIGRATION.md); the web
 * tier is a static SPA with no origin server. Convex HTTP actions authenticate
 * with an `Authorization: Bearer` token minted from Clerk's `convex` JWT
 * template — a cookie would not reach them — which is what this attaches.
 *
 * The base defaults to the deployment's own HTTP host, derived from
 * `NEXT_PUBLIC_CONVEX_URL`: Convex serves functions on `<name>.convex.cloud`
 * and HTTP actions on `<name>.convex.site`. Deriving it means a preview
 * deployment points at its own backend with no extra configuration, which a
 * separately-set variable would get wrong every time a branch spun up a new
 * Convex preview. `NEXT_PUBLIC_CONVEX_API_BASE` overrides it for a custom
 * domain such as `https://api.roamtogether.ai`.
 */
export function convexHttpBase(): string {
  const explicit = (process.env.NEXT_PUBLIC_CONVEX_API_BASE ?? '').trim();
  if (explicit) return explicit.replace(/\/+$/, '');

  const convexUrl = (process.env.NEXT_PUBLIC_CONVEX_URL ?? '').trim();
  if (!convexUrl) return '';
  return convexUrl.replace(/\/+$/, '').replace(/\.convex\.cloud$/, '.convex.site');
}

/**
 * Resolve a stored media path onto the backend that actually serves it.
 *
 * Place photos are persisted as the RELATIVE path `/api/places/photo?…`
 * (packages/ai/src/places/discovery.ts) — a shape that dates from when the web
 * tier had server routes. It no longer does: the SPA answers every unknown
 * path with index.html, so a relative photo path returns HTML to an `<img>`
 * and every photo silently renders as its unavailable-placeholder. The proxy
 * itself is fine and deliberately anonymous (the HMAC signature is the
 * authorization, so no bearer token is needed) — only the host was missing.
 *
 * Absolute URLs pass through untouched.
 */
export function resolveApiMediaUrl(url: string): string {
  if (!url.startsWith('/api/')) return url;
  const base = convexHttpBase();
  return base ? `${base}${url}` : url;
}

export type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

/** Mirrors Clerk `getToken` options used by Convex HTTP auth. */
export type TokenMinterOptions = {
  template?: string;
  skipCache?: boolean;
};

/** Minted from Clerk's `convex` JWT template; null when unavailable. */
export type TokenMinter = (options?: TokenMinterOptions) => Promise<string | null>;

const AUTH_TOKEN_RETRY_MS = 2_500;
const AUTH_TOKEN_POLL_MS = 100;

/**
 * Convex HTTP rejects a malformed/non-JWT bearer before our httpAction runs,
 * returning 500 without CORS headers. The browser then reports an opaque
 * "Failed to fetch" instead of a readable 401.
 */
export function isUsableConvexAuthToken(token: string | null | undefined): token is string {
  if (!token?.trim()) return false;
  const parts = token.split('.');
  return parts.length === 3 && parts.every((part) => part.length > 0);
}

/**
 * Match `ConvexProviderWithClerk` (`convex/react-clerk`): when Clerk already
 * issued a session whose JWT audience is `convex`, mint with `skipCache` only;
 * otherwise request the dedicated `convex` template.
 */
export function buildConvexAuthTokenOptions(
  sessionClaims: Record<string, unknown> | null | undefined,
  skipCache = false,
): TokenMinterOptions {
  if (sessionClaims?.aud === 'convex') return { skipCache };
  return { template: 'convex', skipCache };
}

function isOpaqueNetworkFailure(error: unknown): boolean {
  if (!(error instanceof Error) || error.name === 'AbortError') return false;
  const message = error.message.trim();
  return (
    /failed to fetch/i.test(message) ||
    /networkerror/i.test(message) ||
    /^load failed$/i.test(message)
  );
}

/**
 * Clerk can still be hydrating when a signed-in user focuses a field and types.
 * Poll briefly before giving up. Only return JWT-shaped tokens — attaching an
 * opaque session string makes Convex HTTP fail without CORS (see above).
 */
export async function mintConvexAuthToken(
  getToken: TokenMinter,
  sessionClaims?: Record<string, unknown> | null,
  skipCache = false,
): Promise<string | null> {
  const read = () =>
    getToken(buildConvexAuthTokenOptions(sessionClaims, skipCache)).catch(() => null);

  let token = await read();
  if (isUsableConvexAuthToken(token)) return token;

  if (skipCache) return null;

  const deadline = Date.now() + AUTH_TOKEN_RETRY_MS;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, AUTH_TOKEN_POLL_MS));
    token = await read();
    if (isUsableConvexAuthToken(token)) return token;
  }
  return null;
}

/** Map HTTP status codes to user-facing copy for Convex HTTP actions. */
export function apiHttpErrorMessage(status: number, bodyError?: string): string {
  const trimmed = bodyError?.trim();
  if (status === 401) {
    return trimmed && trimmed !== 'Not authorized'
      ? trimmed
      : 'Your session expired. Sign in again to continue.';
  }
  if (status === 403) return trimmed || 'You do not have access to do that.';
  if (status === 429) return trimmed || 'Too many requests. Please wait a moment and try again.';
  if (status >= 500) {
    return trimmed || 'Something went wrong on our end. Please try that again.';
  }
  return trimmed || `Request failed (${status}).`;
}

/** Turn a failed `fetch()` into something actionable instead of "Failed to fetch". */
export function describeApiFetchFailure(error: unknown): string {
  if (!(error instanceof Error)) return 'Request failed.';
  if (error.name === 'AbortError') return error.message || 'Request cancelled.';
  const message = error.message.trim();
  if (
    /failed to fetch/i.test(message) ||
    /networkerror/i.test(message) ||
    /^load failed$/i.test(message)
  ) {
    return 'Could not reach Roam right now. Check your connection and try again.';
  }
  return message || 'Request failed.';
}

export async function readApiJsonBody<T extends { error?: string } = { error?: string }>(
  response: Response,
): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    return {} as T;
  }
}

export async function assertApiOk(response: Response, body?: { error?: string }): Promise<void> {
  if (response.ok) return;
  const payload = body ?? (await readApiJsonBody(response));
  throw new Error(apiHttpErrorMessage(response.status, payload.error));
}

/**
 * Route one request to the Convex HTTP host, with a bearer token when one can
 * be minted.
 *
 * A token is unavailable for an anonymous visitor, and under
 * `NEXT_PUBLIC_DEV_AUTH_BYPASS` where the auth facade's `getToken` returns
 * null. The request still goes to Convex: there is no same-origin handler to
 * fall back to any more, and a route that permits anonymous access (the signed
 * photo proxy) should still be reachable. Routes that require identity answer
 * 401, which is the honest result.
 *
 * Kept as a plain function rather than living inside the hook so the routing
 * rules can be tested without a DOM.
 */
async function routeApiRequestOnce(
  base: string,
  path: string,
  init: RequestInit | undefined,
  getToken: TokenMinter,
  sessionClaims: Record<string, unknown> | null | undefined,
  skipCache: boolean,
): Promise<Response> {
  const token = await mintConvexAuthToken(getToken, sessionClaims, skipCache);
  if (!isUsableConvexAuthToken(token)) return fetch(`${base}${path}`, init);

  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return fetch(`${base}${path}`, { ...init, headers });
}

export async function routeApiRequest(
  path: string,
  init: RequestInit | undefined,
  getToken: TokenMinter,
  sessionClaims?: Record<string, unknown> | null,
): Promise<Response> {
  const base = convexHttpBase();
  if (!base) return fetch(path, init);

  try {
    return await routeApiRequestOnce(base, path, init, getToken, sessionClaims, false);
  } catch (error) {
    if (!isOpaqueNetworkFailure(error)) throw error;
    // A stale or mis-minted bearer can make Convex HTTP return 500 without
    // CORS, which surfaces as Failed to fetch. Retry once with a fresh token
    // using the same Clerk options Convex WebSocket auth uses.
    return routeApiRequestOnce(base, path, init, getToken, sessionClaims, true);
  }
}

/**
 * React binding for {@link routeApiRequest}.
 *
 * The returned function is referentially stable for the lifetime of the
 * component — `getToken` is read through a ref rather than captured — so it is
 * safe to list in a `useEffect` dependency array. Depending on `getToken`
 * directly would re-create the callback whenever Clerk re-rendered, and any
 * effect depending on it would re-run in a loop.
 */
export function useApiFetch(): ApiFetch {
  const { getToken, sessionClaims } = useAuth();
  const getTokenRef = useRef(getToken);
  const sessionClaimsRef = useRef(sessionClaims);
  getTokenRef.current = getToken;
  sessionClaimsRef.current = sessionClaims;

  return useCallback(
    (path: string, init?: RequestInit) =>
      routeApiRequest(
        path,
        init,
        ((options?: TokenMinterOptions) => getTokenRef.current(options)) as TokenMinter,
        sessionClaimsRef.current,
      ),
    [],
  );
}
